import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import CompanyContext from '../../auth/CompanyContext';
import Loading from '../Loading/Loading';
import * as utilities from '../../utilities';
import './Contacts2.css';
import ContactPercentageRing from './ContactPercentageRing';
import {
    getEngagementInsights,
    getFieldValues,
    getRelationshipTotals,
    isValidEmail,
    toNumber
} from './contactInsights';

const CORE_FIELDS = [
    'Contact Name',
    'Title',
    'Phone Number',
    'Phone Number (Office)',
    'Phone Number (Mobile)',
    'Phone Number (Personal)',
    'Phone Number (Home)',
    'Email Address',
    'Email Address (Office)',
    'Email Address (Personal)',
    'LinkedIn URL'
];

const EMAIL_FIELDS = [
    'Email Address',
    'Email Address (Office)',
    'Email Address (Personal)'
];

const ContactsCore2 = ({
    clientToken,
    idToken,
    contactGUID,
    isActive,
    row,
    editCategoryCommentsArray = [],
    companies = [],
    selectedContactHouseholds = [],
    contactAccountsLoading = false,
    householdsLoading = false,
    companySettings,
    crmEnabled = false,
    onNotesCountChange
}) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [investmentLoading, setInvestmentLoading] = useState(false);
    const [investmentMarketValue, setInvestmentMarketValue] = useState(null);
    const [investmentAsOf, setInvestmentAsOf] = useState('');
    const [marketingData, setMarketingData] = useState({
        workflows: [],
        activities: [],
        opportunities: []
    });
    const [marketingLoading, setMarketingLoading] = useState(false);

    const fieldValues = useMemo(() => (
        getFieldValues(row, editCategoryCommentsArray)
    ), [editCategoryCommentsArray, row]);

    const selectedHouseholdKeys = useMemo(() => (
        (selectedContactHouseholds || [])
            .map(item => String(item || '').trim())
            .filter(Boolean)
    ), [selectedContactHouseholds]);

    const formatCurrency = useCallback((value) => {
        const numberValue = toNumber(value);
        return numberValue.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }, []);

    const formatDate = useCallback((value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
    }, []);

    const getLatestHoldingsDate = useCallback(async (household) => {
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/posDates`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            },
            body: JSON.stringify({
                port: '',
                household
            })
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code === 200 && response.data && response.data.length > 0) return response.data[0].fDate;
        return '';
    }, [idToken, companyCtx.company]);

    const getHoldings = useCallback(async (household, dateValue) => {
        const dateObj = dateValue ? new Date(dateValue) : null;
        const params = new URLSearchParams({
            household,
            date: dateObj && !Number.isNaN(dateObj.getTime()) ? dateObj.toISOString() : ''
        });
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/holdingsSnapshot?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            }
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code !== 200) return [];
        return response.data || [];
    }, [idToken, companyCtx.company]);

    const getHoldingMarketValue = useCallback((holding = {}) => (
        toNumber(
            holding.marketValue
            ?? holding.MarketValue
            ?? holding.market_value
            ?? holding.value
            ?? holding.Value
        )
    ), []);

    const loadNotes = useCallback(() => {
        if (!isActive || !contactGUID || !clientToken || !companyCtx.company) return;

        setNotesLoading(true);
        fetch(`/api/contactsNotes/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                const nextNotes = results?.data || [];
                setNotes(nextNotes);
                if (typeof onNotesCountChange === 'function') onNotesCountChange(nextNotes.length);
            })
            .catch(() => {
                setNotes([]);
                utilities.addToast('Unable to load contact notes.', 5, 'danger');
            })
            .finally(() => setNotesLoading(false));
    }, [isActive, contactGUID, clientToken, companyCtx.company, onNotesCountChange]);

    const loadMarketingData = useCallback(() => {
        if (!isActive || !contactGUID || !clientToken || !companyCtx.company) return;

        setMarketingLoading(true);
        fetch(`/api/contacts/marketingActivities/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                setMarketingData({
                    workflows: results.data?.workflows || [],
                    activities: results.data?.activities || [],
                    opportunities: results.data?.opportunities || []
                });
            })
            .catch(() => {
                setMarketingData({
                    workflows: [],
                    activities: [],
                    opportunities: []
                });
                utilities.addToast('Unable to load contact engagement data.', 5, 'danger');
            })
            .finally(() => setMarketingLoading(false));
    }, [isActive, contactGUID, clientToken, companyCtx.company]);

    const loadInvestmentMarketValue = useCallback(async () => {
        if (!isActive) return;
        if (!crmEnabled || !hasCompanyPortalDatabase || !idToken || !companyCtx.company) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            return;
        }
        if (contactAccountsLoading || householdsLoading) return;
        if (selectedHouseholdKeys.length === 0) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            return;
        }

        setInvestmentLoading(true);
        try {
            let totalValue = 0;
            let latestDate = '';
            const values = await Promise.all(selectedHouseholdKeys.map(async household => {
                const date = await getLatestHoldingsDate(household);
                const holdings = await getHoldings(household, date);
                return {
                    date,
                    value: holdings.reduce((sum, holding) => sum + getHoldingMarketValue(holding), 0)
                };
            }));

            values.forEach(item => {
                totalValue += item.value;
                if (item.date && (!latestDate || new Date(item.date) > new Date(latestDate))) latestDate = item.date;
            });

            setInvestmentMarketValue(totalValue);
            setInvestmentAsOf(latestDate);
        } catch (err) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            utilities.addToast('Unable to load investment market value from Portal+.', 5, 'danger');
        } finally {
            setInvestmentLoading(false);
        }
    }, [
        isActive,
        crmEnabled,
        hasCompanyPortalDatabase,
        idToken,
        companyCtx.company,
        contactAccountsLoading,
        householdsLoading,
        selectedHouseholdKeys,
        getLatestHoldingsDate,
        getHoldings,
        getHoldingMarketValue
    ]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        loadMarketingData();
    }, [loadMarketingData]);

    useEffect(() => {
        loadInvestmentMarketValue();
    }, [loadInvestmentMarketValue]);

    const relationshipTotals = useMemo(() => (
        getRelationshipTotals(marketingData.opportunities || [])
    ), [marketingData.opportunities]);

    const engagement = useMemo(() => (
        getEngagementInsights({
            notes,
            marketingData,
            companies,
            fieldValues,
            relationshipTotals
        })
    ), [notes, marketingData, relationshipTotals, companies, fieldValues]);

    const renderFieldValue = (field) => {
        const value = String(fieldValues[field] || '').trim();
        if (EMAIL_FIELDS.includes(field)) {
            if (!isValidEmail(value)) return <span className="text-danger">{value} (invalid)</span>;
            return <a href={`mailto:${value}`}>{value}</a>;
        }
        if (field === 'LinkedIn URL') return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
        return value;
    };

    const renderStatusWidget = (percentage, items) => (
        <Row className="contactsCoreFieldRow">
            <Col sm={4} className="contactsCoreFieldLabel">
                <ContactPercentageRing percentage={percentage} />
            </Col>
            <Col sm={8}>
                <ul className="contactsCoreWidgetReasons small mb-0">
                    {(items && items.length > 0 ? items : ['No Active Relationship Signals']).map(item => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            </Col>
        </Row>
    );

    const recentNotes = notes.slice(0, 5);
    const widgetsLoading = notesLoading
        || marketingLoading
        || investmentLoading
        || contactAccountsLoading
        || householdsLoading;

    if (!isActive) return null;

    return (
        <Row className="contactsCore text-left">
            <Col md={4}>
                <Card className="mb-3">
                    <CardHeader>
                        <h4>Contact Info</h4>
                    </CardHeader>
                    <CardBody>
                        {companies.length > 0 && (
                            <Row className="contactsCoreFieldRow">
                                <Col sm={4} className="contactsCoreFieldLabel">Company</Col>
                                <Col sm={8}>
                                    {companies.map((company, index) => (
                                    <div key={`${company.secID}_${index}`}>
                                        <Link to={{ pathname: `/demographics/${company.secID}`, state: {} }}>
                                            {company.primaryBusinessName || company.secID}
                                        </Link>
                                    </div>
                                    ))}
                                </Col>
                            </Row>
                        )}
                        {CORE_FIELDS
                            .filter(field => String(fieldValues[field] || '').trim() !== '')
                            .map(field => (
                                <Row key={field} className="contactsCoreFieldRow">
                                    <Col sm={4} className="contactsCoreFieldLabel">{field}</Col>
                                    <Col sm={8}>{renderFieldValue(field)}</Col>
                                </Row>
                            ))}
                    </CardBody>
                </Card>

                <Card className="mb-3">
                    <CardHeader>
                        <h4>Engagement Level</h4>
                    </CardHeader>
                    <CardBody>
                        {widgetsLoading ? (
                            <Loading />
                        ) : (
                            renderStatusWidget(engagement.percentage, engagement.reasons)
                        )}
                    </CardBody>
                </Card>

            </Col>

            <Col md={5}>
                <Card className="mb-3">
                    <CardHeader>
                        <h4>Notes</h4>
                    </CardHeader>
                    <CardBody>
                        {notesLoading ? <Loading /> : null}
                        {!notesLoading && recentNotes.length === 0 ? <Alert color="light">No notes have been added for this contact.</Alert> : null}
                        {!notesLoading && recentNotes.map(note => (
                            <div key={note.id} className="contactsCoreNote">
                                <div>{note.notes}</div>
                                <div className="text-muted small">
                                    {formatDate(note.date || note.timestamp)}
                                    {note.fullName ? ` · ${note.fullName}` : ''}
                                </div>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </Col>

            <Col md={3}>
                <Card className="mb-3">
                    <CardHeader>
                        <h4>Relationship Value</h4>
                    </CardHeader>
                    <CardBody>
                        {marketingLoading ? (
                            <Loading />
                        ) : (
                            <>
                                <div className="contactsCoreRelationshipValue">
                                    {formatCurrency(relationshipTotals.total)}
                                </div>
                                <div className="text-muted">
                                    Sales: {formatCurrency(relationshipTotals.sales)}
                                    {relationshipTotals.salesCount > 0 ? ` (${relationshipTotals.salesCount})` : ''}
                                </div>
                                <div className="text-muted">
                                    Opportunities: {formatCurrency(relationshipTotals.opportunities)}
                                    {relationshipTotals.opportunityCount > 0 ? ` (${relationshipTotals.opportunityCount})` : ''}
                                </div>
                                {relationshipTotals.excludedCount > 0 ? (
                                    <div className="text-muted small mt-2">
                                        {relationshipTotals.excludedCount} non-value status record{relationshipTotals.excludedCount === 1 ? '' : 's'} excluded from value.
                                    </div>
                                ) : null}
                            </>
                        )}
                    </CardBody>
                </Card>

                {selectedHouseholdKeys.length > 0 ? (
                    <Card className="mb-3">
                        <CardHeader>
                            <h4>Investment Market Value</h4>
                        </CardHeader>
                        <CardBody>
                            {investmentLoading || contactAccountsLoading || householdsLoading ? (
                                <Loading />
                            ) : (
                                <>
                                    <div className="contactsCoreRelationshipValue">
                                        {investmentMarketValue == null ? '—' : formatCurrency(investmentMarketValue)}
                                    </div>
                                    <div className="text-muted">
                                        {selectedHouseholdKeys.length} mapped account{selectedHouseholdKeys.length === 1 ? '' : 's'}
                                    </div>
                                    {investmentAsOf ? <div className="text-muted small">As of {formatDate(investmentAsOf)}</div> : null}
                                    {!crmEnabled || !hasCompanyPortalDatabase ? (
                                        <Alert color="light" className="mt-3 mb-0">Portal+ is not available for this company.</Alert>
                                    ) : null}
                                </>
                            )}
                        </CardBody>
                    </Card>
                ) : null}
            </Col>
        </Row>
    );
};

export default ContactsCore2;
